import type { Disposable } from '../core/Event';
import type { SlotGameModel } from '../model/slot/SlotGameModel';
import { AudioBus } from './AudioBus';

/**
 * Conventional sound-event aliases the slot scene plays. Themes that ship
 * audio bundles should register entries under these keys so the binding
 * picks them up automatically.
 */
export const SlotSoundIds = {
  spinStart: 'slot.spin-start',
  reelStop: 'slot.reel-stop',
  win: 'slot.win',
  gambleAccepted: 'slot.gamble-accepted',
  gambleResolvedWin: 'slot.gamble-win',
  gambleResolvedLose: 'slot.gamble-lose',
  collectStarted: 'slot.collect-started',
  collectCompleted: 'slot.collect-completed',
  notEnoughCoins: 'slot.not-enough-coins',
  /** Plays when scatter triggers free spins — bigger, "you triggered the
   * bonus" sting. */
  freeSpinsGranted: 'slot.free-spins-granted',
} as const;

/**
 * Subscribes to {@link SlotGameModel} events and plays the corresponding
 * audio cues via an {@link AudioBus}. Pure listener — no Pixi state — so
 * scenes can construct one in their build phase and dispose it in destroy.
 *
 * <p>Themes without audio bundles (the bundled v0 default + midnight)
 * simply produce no sound; the binding still attaches but every
 * {@link AudioBus#play} call falls through to "alias not loaded".
 */
export class SlotAudioBinding {
  private readonly subscriptions: Disposable[] = [];

  constructor(model: SlotGameModel, bus: AudioBus = AudioBus.shared) {
    this.subscriptions.push(
      model.events.on('spin-started', () => bus.play(SlotSoundIds.spinStart)),
      model.events.on('spin-landed', (event) => {
        if (event.settlement.totalWinnings > 0) {
          bus.play(SlotSoundIds.win);
        }
      }),
      model.events.on('gamble-accepted', () => bus.play(SlotSoundIds.gambleAccepted)),
      model.events.on('gamble-resolved', (event) => {
        bus.play(
          event.result.outcome === 'win'
            ? SlotSoundIds.gambleResolvedWin
            : SlotSoundIds.gambleResolvedLose,
        );
      }),
      model.events.on('collect-started', () => bus.play(SlotSoundIds.collectStarted)),
      model.events.on('collect-completed', () => bus.play(SlotSoundIds.collectCompleted)),
      model.events.on('not-enough-coins', () => bus.play(SlotSoundIds.notEnoughCoins)),
      model.events.on('free-spins-granted', () => bus.play(SlotSoundIds.freeSpinsGranted)),
    );
  }

  dispose(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
  }
}
